import type { CognitoAuth } from '@classgo/shared/auth';
import { createContext, type ReactNode, useContext, useSyncExternalStore } from 'react';

import type { AuthState, AuthStore } from './types';

const AuthStoreContext = createContext<{ store: AuthStore; cognito: CognitoAuth } | null>(null);

/**
 * Makes the auth store and the Cognito wrapper available to screens; both are created once in
 * the app's `main.tsx`.
 */
export const AuthProvider = ({
  store,
  cognito,
  children,
}: {
  store: AuthStore;
  cognito: CognitoAuth;
  children: ReactNode;
}) => <AuthStoreContext.Provider value={{ store, cognito }}>{children}</AuthStoreContext.Provider>;

/**
 * The Cognito wrapper of this app's client.
 *
 * @returns The wrapper.
 */
export const useCognito = (): CognitoAuth => {
  const value = useContext(AuthStoreContext);
  if (!value) throw new Error('useCognito needs an AuthProvider');
  return value.cognito;
};

/**
 * The auth store (imperative API: complete a sign-in, sign out locally).
 *
 * @returns The store.
 */
export const useAuthStore = (): AuthStore => {
  const value = useContext(AuthStoreContext);
  if (!value) throw new Error('useAuthStore needs an AuthProvider');
  return value.store;
};

/**
 * The current session state, re-rendering on change.
 *
 * @returns The snapshot.
 */
export const useAuthState = (): AuthState => {
  const store = useAuthStore();
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
};
